import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ResizablePanels.css';

const STORAGE_KEY = 'masterPanelWidth.v2';

/**
 * ResizablePanels Component
 *
 * Provides two panels with a draggable splitter between them.
 * The left panel (master) can be resized by dragging the splitter.
 *
 * @param {ReactNode} leftPanel - Content for the left (master) panel
 * @param {ReactNode} rightPanel - Content for the right (detail) panel
 * @param {number} defaultLeftWidth - Default width percentage for left panel (default: 40)
 * @param {number} minLeftWidth - Minimum width percentage for left panel (default: 20)
 * @param {number} maxLeftWidth - Maximum width percentage for left panel (default: 80)
 * @param {boolean} leftCollapsed - Whether the left panel is collapsed
 */
function ResizablePanels({
  leftPanel,
  rightPanel,
  defaultLeftWidth = 40,
  minLeftWidth = 20,
  maxLeftWidth = 80,
  leftCollapsed = false
}) {
  const { t } = useTranslation('common');
  const [leftWidth, setLeftWidth] = useState(defaultLeftWidth);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    // Load saved width from localStorage (v2: default changed 30% -> 40%;
    // the old key was written on every mount, so it can't tell a user choice from the old default)
    const savedWidth = localStorage.getItem(STORAGE_KEY);
    if (savedWidth) {
      setLeftWidth(parseFloat(savedWidth));
    }
  }, []);

  useEffect(() => {
    // Save width to localStorage when it changes
    localStorage.setItem(STORAGE_KEY, leftWidth.toString());
  }, [leftWidth]);

  const handlePointerDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  // Double-click the splitter to go back to the default width
  const handleDoubleClick = () => setLeftWidth(defaultLeftWidth);

  useEffect(() => {
    const handlePointerMove = (e) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newLeftWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;

      // Constrain the width between min and max
      if (newLeftWidth >= minLeftWidth && newLeftWidth <= maxLeftWidth) {
        setLeftWidth(newLeftWidth);
      }
    };

    const handlePointerUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      // Prevent text selection while dragging
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    }

    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging, minLeftWidth, maxLeftWidth]);

  // No detail open (rightPanel is null/false in list mode): the list gets the full width.
  // Every page renders either the list or the detail, so splitting an empty right half
  // only made tables scroll sideways.
  const hasDetail = Boolean(rightPanel);

  if (!hasDetail && !leftCollapsed) {
    return (
      <div className="resizable-panels" ref={containerRef}>
        <div className="resizable-panel-left full-width" style={{ width: '100%' }}>
          {leftPanel}
        </div>
      </div>
    );
  }

  return (
    <div className="resizable-panels" ref={containerRef}>
      <div
        className={`resizable-panel-left ${leftCollapsed ? 'collapsed' : ''}`}
        style={{
          width: leftCollapsed ? '0' : `${leftWidth}%`,
          minWidth: leftCollapsed ? '0' : `${minLeftWidth}%`,
          maxWidth: leftCollapsed ? '0' : `${maxLeftWidth}%`
        }}
      >
        {!leftCollapsed && leftPanel}
      </div>

      {!leftCollapsed && (
        <div
          className={`resizable-splitter ${isDragging ? 'dragging' : ''}`}
          onPointerDown={handlePointerDown}
          onDoubleClick={handleDoubleClick}
          title={t('resizablePanels.splitterHint')}
        >
          <div className="splitter-handle">
            <span className="splitter-dots">⋮</span>
          </div>
        </div>
      )}

      <div
        className="resizable-panel-right"
        style={{
          width: leftCollapsed ? '100%' : `calc(${100 - leftWidth}% - 5px)`
        }}
      >
        {rightPanel}
      </div>
    </div>
  );
}

export default ResizablePanels;